import React, { useState, useEffect } from "react";
import { Modal } from "../shared/modal";
import { ChevronLeft } from "../icons/chevron-left";
import { ChevronRight } from "../icons/chevron-right";
import { cn } from "../utls/utils";

interface PDFFile {
  url: string;
  fileName?: string;
}

interface PDFViewerProps {
  isOpen: boolean;
  onClose: () => void;
  pdfUrl: string;
  fileName?: string;
  pdfFiles?: PDFFile[];
  currentIndex?: number;
}

export function PDFViewer({
  isOpen,
  onClose,
  pdfUrl,
  fileName = "Document",
  pdfFiles = [],
  currentIndex = 0,
}: PDFViewerProps) {
  const [activeIndex, setActiveIndex] = useState(currentIndex);
  const hasMultiplePdfs = pdfFiles.length > 1;
  
  // Use the current pdfUrl if no pdfFiles array provided
  const currentPdf = hasMultiplePdfs 
    ? pdfFiles[activeIndex] 
    : { url: pdfUrl, fileName: fileName };

  // Reset active index when modal opens or PDF files change
  useEffect(() => {
    if (isOpen) {
      setActiveIndex(currentIndex);
    }
  }, [isOpen, currentIndex, pdfFiles]);
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (hasMultiplePdfs) {
        if (event.key === "ArrowLeft") {
          navigatePrevious();
        } else if (event.key === "ArrowRight") {
          navigateNext();
        }
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, activeIndex, hasMultiplePdfs]);
  
  const navigatePrevious = () => {
    if (hasMultiplePdfs && activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }
  };
  
  const navigateNext = () => {
    if (hasMultiplePdfs && activeIndex < pdfFiles.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={currentPdf.fileName || fileName}
    >
      <div className="relative bg-white w-full h-[85vh] overflow-auto">
        {/* Previous PDF button */}
        {hasMultiplePdfs && (
          <button
            className={cn(
              "absolute left-4 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full",
              "bg-terracotta-500/75 text-white hover:bg-terracotta-600/90 transition-colors",
              activeIndex === 0 && "opacity-50 cursor-not-allowed"
            )}
            onClick={navigatePrevious}
            disabled={activeIndex === 0}
            aria-label="Previous PDF"
          >
            <ChevronLeft active={true} />
          </button>
        )}
        
        <iframe
          src={`${currentPdf.url}#toolbar=1&navpanes=1&scrollbar=1`}
          className="w-full h-full border-0"
          title={currentPdf.fileName || fileName}
        />
        
        {/* Next PDF button */}
        {hasMultiplePdfs && (
          <button
            className={cn(
              "absolute right-4 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full",
              "bg-terracotta-500/75 text-white hover:bg-terracotta-600/90 transition-colors",
              activeIndex === pdfFiles.length - 1 && "opacity-50 cursor-not-allowed"
            )}
            onClick={navigateNext}
            disabled={activeIndex === pdfFiles.length - 1}
            aria-label="Next PDF"
          >
            <ChevronRight active={true} />
          </button>
        )}
        
        {/* PDF counter */}
        {hasMultiplePdfs && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-terracotta-500/75 text-white px-3 py-1 rounded-full">
            {activeIndex + 1} / {pdfFiles.length}
          </div>
        )}
      </div>
    </Modal>
  );
}